import * as Popover from '@radix-ui/react-popover';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import Input from '@/components/Input.jsx';
import IconV2 from '@/components/v2/IconV2.jsx';
import Spinner from '@/components/Spinner';
import KeyHandler from '@/components/KeyHandler';
import Toggle from '@/components/Toggle';
import Tag from '@/components/Tag';
import { FaInbox, FiSearch } from '@/components/react-icons/main';

const SelectPopoverV2 = ({ children, options = [], onSelect, renderOption, footerActions = [], useSearch = true, searchValue, onSearch, searchPlaceholder = 'Search...', loading = false, emptyLabel = 'No results found', emptyIcon = FaInbox, className = '', width = 324, maxHeight = 300, clearSearchOnClose = false, open, onOpenChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [internalSearch, setInternalSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const searchInputRef = useRef(null);
  const itemsContainerRef = useRef(null);

  const resolvedOpen = open !== undefined ? open : isOpen;
  const setResolvedOpen = onOpenChange || setIsOpen;

  const search = searchValue !== undefined ? searchValue : internalSearch;

  const handleSearchChange = (event) => {
    const nextValue = event?.target?.value ?? '';
    if (onSearch) {
      onSearch(nextValue);
    }
    if (searchValue === undefined) {
      setInternalSearch(nextValue);
    }
  };

  const filteredOptions = useMemo(() => {
    if (!useSearch || onSearch) {
      return options;
    }
    const trimmed = (search || '').trim().toLowerCase();
    if (!trimmed) {
      return options;
    }
    return options.filter((option) => {
      if (option?.type === 'separator') {
        return true;
      }
      const label = String(option?.label || option?.value || '').toLowerCase();
      const description = String(option?.description || '').toLowerCase();
      return label.includes(trimmed) || description.includes(trimmed);
    });
  }, [options, useSearch, onSearch, search]);

  const selectableOptions = useMemo(() => filteredOptions.filter((option) => option?.type !== 'separator' && option?.disabled !== true), [filteredOptions]);
  const maxIndex = selectableOptions.length - 1 + footerActions.length;
  const optionRefs = useMemo(() => selectableOptions.map(() => ({ current: null })), [selectableOptions.length]);

  useEffect(() => {
    if (!resolvedOpen) {
      return;
    }
    if (selectableOptions.length === 0) {
      setSelectedIndex(useSearch ? -1 : 0);
      return;
    }
    setSelectedIndex((prev) => {
      if (useSearch && prev === -1) {
        return prev;
      }
      if (prev < 0) {
        return 0;
      }
      if (prev > maxIndex) {
        return maxIndex;
      }
      return prev;
    });
  }, [selectableOptions.length, resolvedOpen, maxIndex, useSearch]);

  const handleClose = () => {
    if (clearSearchOnClose && search) {
      handleSearchChange({ target: { value: '' } });
    }
    setResolvedOpen(false);
  };

  const scrollSelectedItemIntoView = (index) => {
    if (index < 0 || index >= optionRefs.length || !itemsContainerRef.current) {
      return;
    }
    const selectedElement = optionRefs[index]?.current;
    if (!selectedElement) {
      return;
    }

    const container = itemsContainerRef.current;
    const elementTop = selectedElement.offsetTop;
    const elementHeight = selectedElement.offsetHeight;
    const containerHeight = container.offsetHeight;

    const topPadding = elementHeight * 3;
    const bottomPadding = elementHeight / 2;
    const minScroll = elementTop - topPadding;
    const maxScroll = elementTop - containerHeight + elementHeight + bottomPadding;
    const currentScroll = container.scrollTop;

    if (currentScroll <= minScroll || currentScroll >= maxScroll) {
      container.scrollTo({
        top: currentScroll > minScroll ? minScroll : maxScroll,
        behavior: 'smooth'
      });
    }
  };

  const moveSelection = (direction) => {
    if (selectableOptions.length === 0 && footerActions.length === 0) {
      return;
    }

    let newIndex = selectedIndex + direction;
    const minIndex = useSearch ? -1 : 0;

    if (newIndex < minIndex) {
      newIndex = maxIndex;
    } else if (newIndex > maxIndex) {
      newIndex = minIndex;
    }

    setSelectedIndex(newIndex);
    if (useSearch && newIndex === -1) {
      searchInputRef.current?.focus();
      return;
    }
    searchInputRef.current?.blur();
    scrollSelectedItemIntoView(newIndex);
  };

  const handleOptionSelect = (option) => {
    if (!option || option?.disabled === true) {
      return;
    }
    const handler = option?.onSelect || onSelect;
    handler?.(option);
    handleClose();
  };

  const handleFooterAction = (action) => {
    if (!action) {
      return;
    }
    action?.onClick?.();
    if (action?.closeOnClick !== false) {
      handleClose();
    }
  };

  const keyMap = useMemo(
    () => ({
      ArrowUp: (e) => {
        e.preventDefault();
        moveSelection(-1);
      },
      ArrowDown: (e) => {
        e.preventDefault();
        moveSelection(1);
      },
      Enter: (e) => {
        e.preventDefault();
        if (selectedIndex < 0) {
          return;
        }
        if (selectedIndex < selectableOptions.length) {
          handleOptionSelect(selectableOptions[selectedIndex]);
          return;
        }
        const footerIndex = selectedIndex - selectableOptions.length;
        handleFooterAction(footerActions[footerIndex]);
      }
    }),
    [selectedIndex, selectableOptions, footerActions]
  );

  const renderDefaultOption = (option, isActive, isSelected, optionRef) => {
    const rightContent = option?.right;
    return (
      <div ref={optionRef} data-active={isActive} data-selected={isSelected} className="select-popover-item" onClick={() => handleOptionSelect(option)} tabIndex={0}>
        {option?.icon && <IconV2 icon={option.icon} className="item-icon" />}
        <span className="item-name">{option?.label || option?.value}</span>
        {option?.tag && <Tag className="item-tag">{option.tag}</Tag>}
        {rightContent && <span className="item-right">{rightContent}</span>}
      </div>
    );
  };

  return (
    <Popover.Root open={resolvedOpen} onOpenChange={(nextOpen) => (nextOpen ? setResolvedOpen(nextOpen) : handleClose())}>
      <Popover.Trigger asChild onClick={() => setResolvedOpen(true)}>
        {children}
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={`comp-select-popover-v2 ${className}`} align="start" sideOffset={8} style={{ width }}>
          <div className="body">
            {useSearch && (
              <div className="search">
                <div className="search-icon">
                  <IconV2 icon={FiSearch} variant="light" />
                </div>
                <Input ref={searchInputRef} className="search-input borderless" placeholder={searchPlaceholder} onChange={handleSearchChange} value={search} />
                <div data-loading={loading} className="search-icon">
                  <IconV2 icon={Spinner} />
                </div>
              </div>
            )}
            <div ref={itemsContainerRef} className="items-container" style={{ maxHeight }}>
              {loading && (
                <div className="empty-state">
                  <span>Searching...</span>
                </div>
              )}
              {!loading &&
                (filteredOptions.length === 0 ? (
                  <div className="empty-state">
                    <IconV2 icon={emptyIcon} variant="light" />
                    <span>{emptyLabel}</span>
                  </div>
                ) : (
                  filteredOptions.map((option, idx) => {
                    if (option?.type === 'separator') {
                      return (
                        <span key={`separator-${idx}`} className="select-popover-separator">
                          {option?.label || 'Recents'}
                        </span>
                      );
                    }

                    const optionIndex = selectableOptions.findIndex((item) => item === option);
                    const isActive = selectedIndex === optionIndex;
                    const isSelected = option?.selected === true;
                    const optionRef = optionRefs[optionIndex];
                    const optionKey = option?.id || option?.value || option?.label || idx;

                    if (renderOption) {
                      return <Fragment key={optionKey}>{renderOption({ option, isActive, isSelected, optionRef, onSelect: handleOptionSelect })}</Fragment>;
                    }

                    return <Fragment key={optionKey}>{renderDefaultOption(option, isActive, isSelected, optionRef)}</Fragment>;
                  })
                ))}
            </div>
          </div>

          {footerActions.map((action, index) => (
            <div key={action?.id || index} data-active={selectedIndex === selectableOptions.length + index} className="select-popover-footer-action" onClick={() => handleFooterAction(action)} tabIndex={0}>
              {action?.icon && <IconV2 icon={action.icon} className="footer-action-icon" variant="light" />}
              <span>{action?.text}</span>
              {action?.showToggle && (
                <Toggle
                  className="toggle-action"
                  {...action.toggleProps}
                  onClick={(e) => {
                    e.stopPropagation();
                    action?.toggleProps?.onClick?.(e);
                  }}
                />
              )}
            </div>
          ))}

          {resolvedOpen && <KeyHandler keyMap={keyMap} global={true} active={resolvedOpen} stopPropagation={true} preventDefault={true} />}
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};

export default SelectPopoverV2;
